import { useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import { Box, Button, Typography } from "@mui/material";
import { GridActionsCellItem, GridSortModel } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import {
  AssociatedFacilitiesQuery,
  AssociatedTierIiReportsQuery,
} from "generated-graphql/graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import { safeParseFloat } from "./lib/numberUtils";
import { AddAssociation } from "./AddAssociation";
import {
  FACILITY_ASSOCIATIONS_QUERY,
  TIER_II_REPORT_ASSOCIATIONS_QUERY,
} from "./lib/schema";
import {
  Association,
  FacilityAssociation,
  ReportAssociation,
  PayeeFormInputs,
} from "./lib/types";

const AssociatedEntities = ({
  control,
  tenantId,
  type,
  disabled,
  hideTitle = false,
  titleOverride,
  transactionFee,
}: {
  control: Control<PayeeFormInputs>;
  tenantId: string;
  type: "Report" | "Facility";
  disabled?: boolean;
  hideTitle?: boolean;
  titleOverride?: string;
  transactionFee?: number;
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const { update, remove, append } = useFieldArray({
    control,
    name: "associations",
  });
  const associations = useWatch({ control, name: "associations" });

  // Calculate per-item transaction fee, split evenly across associations with non-zero amounts
  const associationsWithAmount = associations.filter(
    (a: Association) => a.amount > 0
  );
  const perItemTransactionFee =
    transactionFee && associationsWithAmount.length > 0
      ? parseFloat((transactionFee / associationsWithAmount.length).toFixed(2))
      : 0;

  const getAssociationIndex = useCallback(
    (association: Omit<Association, "amount">) =>
      associations.findIndex(
        (a: Association) =>
          a.reportId === association.reportId &&
          a.facilityId === association.facilityId
      ),
    [associations]
  );

  // Find facility-only association (for facility table deletions)
  const getFacilityAssociationIndex = useCallback(
    (facilityId: string) =>
      associations.findIndex(
        (a: Association) => a.facilityId === facilityId && !a.reportId
      ),
    [associations]
  );

  // Find report-only association (for report table deletions)
  const getReportAssociationIndex = useCallback(
    (reportId: string) =>
      associations.findIndex(
        (a: Association) => a.reportId === reportId && !a.facilityId
      ),
    [associations]
  );

  const updateAssociation = (association: Association) => {
    const index = getAssociationIndex(association);
    update(index, association);
  };

  return (
    <Box>
      {showAddModal && (
        <AddAssociation
          onClose={() => setShowAddModal(false)}
          addAssociation={append}
          disabledFacilityIds={associations.map(
            (a: Association) => a.facilityId ?? ""
          )}
          disabledReportIds={associations.map(
            (a: Association) => a.reportId ?? ""
          )}
          tenantId={tenantId}
          type={type}
        />
      )}
      <Box
        sx={{
          justifyContent: hideTitle ? "flex-end" : "space-between",
          display: "flex",
          alignItems: "center",
          mb: 1,
        }}
      >
        {!hideTitle && (
          <Typography variant="h6">
            {titleOverride || `Associated ${pluralize(type, 2)}`}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setShowAddModal(true)}
            disabled={disabled || !tenantId}
          >
            Add {type}
          </Button>
        </Box>
      </Box>
      {type === "Report" ? (
        <ReportTable
          associations={associations
            .filter((a: Association) => a.reportId?.length)
            .map((a: Association) => a as ReportAssociation)}
          deleteReport={(reportId) =>
            remove(getReportAssociationIndex(reportId))
          }
          processRowUpdate={(reportAssociation) =>
            updateAssociation(reportAssociation)
          }
          disabled={disabled}
          perItemTransactionFee={perItemTransactionFee}
        />
      ) : (
        <FacilityTable
          associations={associations
            .filter((a: Association) => a.facilityId?.length)
            .map((a: Association) => a as FacilityAssociation)}
          deleteFacility={(facilityId) =>
            remove(getFacilityAssociationIndex(facilityId))
          }
          processRowUpdate={(facilityAssociation) =>
            updateAssociation(facilityAssociation)
          }
          disabled={disabled}
          perItemTransactionFee={perItemTransactionFee}
        />
      )}
    </Box>
  );
};

type ReportRow =
  AssociatedTierIiReportsQuery["tierIIReports"]["items"][number] & {
    amount: number;
    transactionFeeAllocation: number;
  };

const ReportTable = ({
  associations,
  deleteReport,
  processRowUpdate,
  disabled,
  perItemTransactionFee,
}: {
  associations: ReportAssociation[];
  deleteReport: (reportId: string) => void;
  processRowUpdate: (reportAssociation: ReportAssociation) => void;
  disabled?: boolean;
  perItemTransactionFee?: number;
}) => {
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const reportIds: string[] = associations.map((a) => a.reportId);

  // Skip query only if no reportIds
  const shouldSkipQuery = !reportIds.length;

  const {
    data: reports,
    previousData: previousReports,
    loading,
  } = useQuery(TIER_II_REPORT_ASSOCIATIONS_QUERY, {
    variables: {
      search: `${reportIds.map((fid) => `id:${fid}`).join(" or ")}`,
      sort: [...sortModel],
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: shouldSkipQuery,
  });

  const rows: ReportRow[] =
    (reports ?? previousReports)?.tierIIReports.items
      .filter((r) => associations.some((a) => a.reportId === r.id))
      .map((r) => {
        const amount = associations.find((a) => a.reportId === r.id)?.amount ?? 0;
        return {
          ...r,
          amount,
          // Only allocate transaction fee if this association has a non-zero amount
          transactionFeeAllocation: amount > 0 ? (perItemTransactionFee ?? 0) : 0,
        };
      }) ?? [];

  // Show loading if we have associations but no report data yet
  const isLoadingReportData =
    reportIds.length > 0 && loading && rows.length === 0;

  // Don't render the DataGrid until we have data or are done loading
  if (isLoadingReportData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading report data...
        </Typography>
      </Box>
    );
  }

  return (
    <DataGrid<ReportRow>
      columns={[
        {
          headerName: "Facility",
          field: "facility.name",
          flex: 1,
          valueGetter: (_, row) => row.facility.name,
        },
        {
          headerName: "Facility Address",
          field: "facilityAddress",
          sortable: false,
          flex: 1.2,
          valueGetter: (_, row) => {
            const address = row.facility.streetAddress1;
            if (address) {
              const { streetAddress1, city, state, zip } = row.facility;
              return `${streetAddress1}, ${city}, ${state} ${zip}`;
            }
            return "";
          },
        },
        {
          headerName: "Year",
          field: "reportingYear",
          valueGetter: (_, row) => row.reportingYear,
          flex: 0.4,
        },
        {
          headerName: "Type",
          field: "reportKind",
          valueGetter: (_, row) => row.reportKind,
          flex: 0.4,
        },
        {
          headerName: "Invoice Amount",
          field: "amount",
          flex: 0.6,
          editable: !disabled,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Transaction Fee",
          field: "transactionFeeAllocation",
          flex: 0.6,
          valueGetter: (_, row) => (row.amount > 0 ? perItemTransactionFee : 0),
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Total Amount",
          field: "totalAmount",
          flex: 0.6,
          valueGetter: (_, row) =>
            row.amount + (row.amount > 0 ? (perItemTransactionFee ?? 0) : 0),
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => [
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              onClick={() => deleteReport(params.row.id)}
            />,
          ],
        },
      ]}
      loading={loading}
      rows={rows}
      rowCount={
        reports?.tierIIReports.count ??
        previousReports?.tierIIReports.count ??
        0
      }
      processRowUpdate={(newRow) => {
        // Convert string amount to number using safe parsing that handles commas
        const numericAmount = safeParseFloat(String(newRow.amount)) ?? 0;
        processRowUpdate({ reportId: newRow.id, amount: numericAmount });
        return { ...newRow, amount: numericAmount };
      }}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[10, 25, 50, 100]}
    />
  );
};

type FacilityRow = AssociatedFacilitiesQuery["facilities"]["items"][number] & {
  amount: number;
  transactionFeeAllocation: number;
};

const FacilityTable = ({
  associations,
  deleteFacility,
  processRowUpdate,
  disabled,
  perItemTransactionFee,
}: {
  associations: FacilityAssociation[];
  deleteFacility: (facilityId: string) => void;
  processRowUpdate: (facilityAssociation: FacilityAssociation) => void;
  disabled?: boolean;
  perItemTransactionFee?: number;
}) => {
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const facilityIds: string[] = useMemo(
    () => associations.map((a) => a.facilityId),
    [associations]
  );
  const {
    data: facilities,
    previousData: previousFacilities,
    loading: facilitiesLoading,
  } = useQuery(FACILITY_ASSOCIATIONS_QUERY, {
    variables: {
      search: `${facilityIds.map((fid) => `id:${fid}`).join(" or ")}`,
      sort: [...sortModel],
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: !facilityIds.length,
  });

  const rows: FacilityRow[] =
    (facilities ?? previousFacilities)?.facilities.items
      .filter((f) => associations.some((a) => a.facilityId === f.id))
      .map((f) => {
        const amount = associations.find((a) => a.facilityId === f.id)?.amount ?? 0;
        return {
          ...f,
          amount,
          // Only allocate transaction fee if this association has a non-zero amount
          transactionFeeAllocation: amount > 0 ? (perItemTransactionFee ?? 0) : 0,
        };
      }) ?? [];

  return (
    <DataGrid<FacilityRow>
      columns={[
        {
          headerName: "Facility",
          field: "name",
          flex: 1,
          valueGetter: (_, row) => row.name,
        },
        {
          headerName: "Facility ID",
          field: "customerFacilityId",
          flex: 0.7,
          valueGetter: (_, row) => row.customerFacilityId ?? "",
        },
        {
          headerName: "Facility Address",
          field: "facilityAddress",
          sortable: false,
          flex: 1,
          valueGetter: (_, row) => {
            const facility = row;
            const address = facility?.streetAddress1;
            if (address) {
              const { streetAddress1, city, state, zip } = facility;
              return `${streetAddress1}, ${city}, ${state} ${zip}`;
            }
            return "";
          },
        },
        {
          headerName: "Invoice Amount",
          field: "amount",
          flex: 0.7,
          editable: !disabled,
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Transaction Fee",
          field: "transactionFeeAllocation",
          flex: 0.7,
          valueGetter: (_, row) => (row.amount > 0 ? perItemTransactionFee : 0),
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          headerName: "Total Amount",
          field: "totalAmount",
          flex: 0.7,
          valueGetter: (_, row) =>
            row.amount + (row.amount > 0 ? (perItemTransactionFee ?? 0) : 0),
          valueFormatter(value) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value);
          },
          sortable: false,
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => [
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              onClick={() => deleteFacility(params.row.id)}
            />,
          ],
        },
      ]}
      loading={facilitiesLoading}
      rows={rows}
      rowCount={facilities?.facilities.count ?? 0}
      processRowUpdate={(newRow) => {
        // Convert string amount to number using safe parsing that handles commas
        const numericAmount = safeParseFloat(String(newRow.amount)) ?? 0;
        processRowUpdate({ facilityId: newRow.id, amount: numericAmount });
        return { ...newRow, amount: numericAmount };
      }}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  );
};

export { AssociatedEntities };
